import { Component, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, retry: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

// Boundary global de exception. Sem isso, qualquer throw em render derruba
// a app inteira no "Unexpected Application Error" do React Router. Aqui
// damos opcao de retentar a renderizacao ou voltar ao login.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }): void {
    // Em prod isto vai pra um serviço de erro (Sentry, etc); em dev,
    // console pra facilitar debug.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      return <DefaultFallback error={this.state.error} onReset={this.reset} />;
    }
    return this.props.children;
  }
}

function DefaultFallback({ error, onReset }: { error: Error; onReset: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-6">
      <div className="max-w-lg w-full bg-surface rounded-xl shadow-card border border-danger/30 p-8">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-danger-bg text-danger mb-4">
          <AlertTriangle className="w-5 h-5" />
        </div>
        <h1 className="text-xl font-bold text-text-strong mb-2">Algo deu errado</h1>
        <p className="text-sm text-text-muted mb-4">
          A página encontrou um erro inesperado. Tente recarregar - se persistir, faça logout e
          login novamente.
        </p>
        <details className="mb-5 text-xs">
          <summary className="cursor-pointer text-text-muted hover:text-text-strong">
            Detalhes técnicos
          </summary>
          <pre className="mt-2 p-3 bg-surface-mute rounded-lg text-text-muted font-mono whitespace-pre-wrap break-all">
            {error.message}
            {error.stack ? '\n\n' + error.stack.split('\n').slice(0, 5).join('\n') : ''}
          </pre>
        </details>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-primary text-on-primary hover:bg-primary-hover transition"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Tentar novamente
          </button>
          <button
            type="button"
            onClick={() => {
              try {
                window.sessionStorage.removeItem('just-frotas:access_token');
              } catch {
                /* */
              }
              window.location.href = '/login';
            }}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-border bg-surface hover:bg-surface-mute transition"
          >
            Voltar ao login
          </button>
        </div>
      </div>
    </div>
  );
}
